<template>
  <div class="container-fluid py-4">

    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card-header p-3 d-flex justify-content-between align-items-center flex-wrap gap-2 header-bar">
          <h6 class="text-white m-0">
            {{ $t("employeeprofil.header.title") }}
          </h6>

          <div class="d-flex align-items-center gap-2">

            <!-- ADMIN EDIT -->
            <router-link 
              v-if="canEdit"
              :to="{ path: '/edit-employee', query: { user_id: userId } }">
              <button class="btn btn-sm export-btn">
                <i class="fas fa-pen me-1"></i>
                {{ $t("employeeprofil.header.edit") }}
              </button>
            </router-link>

            <!-- EMPLOYEE EDIT -->
            <button 
              v-if="!canEdit"
              class="btn btn-sm export-btn"
              @click="openEditModal">
              <i class="fas fa-edit me-1"></i>
              {{ $t("employeeprofil.header.editProfile") }}
            </button>

          </div>
        </div>
      </div>
    </div>

    <!-- PROFILE CARD -->
    <div v-if="!loading && userData && personalInfo" class="row g-4">
      <div class="col-12">
        <div class="card employee-card p-4">

          <div class="d-flex flex-wrap align-items-center">
            <div class="profile-img-wrapper me-3 mb-3">
              <img :src="profileImage" @error="onImgError" class="profile-img" alt="Profile"/>
            </div>

            <div class="flex-grow-1 mb-3">
              <h4 class="mb-1">{{ fullName }}</h4>

              <div class="d-flex flex-wrap gap-3 text-muted small">
                <span>
                  <i class="far fa-envelope me-1"></i>{{ userData.email }}
                </span>
                <span v-if="roleLabel">
                  <i class="far fa-user me-1"></i>{{ roleLabel }}
                </span>
              </div>
            </div>
          </div>

          <hr class="my-3"/>

          <div class="row gy-3">
            <div class="col-md-6">
              <label class="field-label">
                {{ $t("employeeprofil.fields.gender") }}
              </label>
              <div class="field-value">
                {{ personalInfo.gender || $t("employeeprofil.values.na") }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="field-label">
                {{ $t("employeeprofil.fields.dateOfBirth") }}
              </label>
              <div class="field-value">
                {{ personalInfo.date_of_birth || $t("employeeprofil.values.na") }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="field-label">
                {{ $t("employeeprofil.fields.phone") }}
              </label>
              <div class="field-value">
                {{ personalInfo.phone_number || $t("employeeprofil.values.na") }}
              </div>
            </div>

            <div class="col-md-6">
              <label class="field-label">
                {{ $t("employeeprofil.fields.placeOfBirth") }}
              </label>
              <div class="field-value">
                {{ personalInfo.place_of_birth || $t("employeeprofil.values.na") }}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-danger" role="status">
        <span class="visually-hidden">
          {{ $t("employeeprofil.loading") }}
        </span>
      </div>
    </div>

    <!-- EMPTY -->
    <div v-if="!loading && (!userData || !personalInfo)" class="text-center text-muted my-5">
      <div>{{ $t("employeeprofil.noData") }}</div>
    </div>

    <!-- ===================== EDIT MODAL ===================== -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal-content">

        <h5 class="mb-3">
          {{ $t("employeeprofil.modal.title") }}
        </h5>

        <div class="mb-3">
          <label>{{ $t("employeeprofil.fields.phone") }}</label>
          <input v-model="form.phone_number" type="text" class="form-control"/>
        </div>

        <div class="mb-3">
          <label>{{ $t("employeeprofil.fields.maritalStatus") }}</label>
          <select v-model="form.marital_status" class="form-control">
            <option value="">
              {{ $t("employeeprofil.maritalStatus.select") }}
            </option>
            <option value="Single">
              {{ $t("employeeprofil.maritalStatus.single") }}
            </option>
            <option value="Married">
              {{ $t("employeeprofil.maritalStatus.married") }}
            </option>
            <option value="Divorced">
              {{ $t("employeeprofil.maritalStatus.divorced") }}
            </option>
            <option value="Widowed">
              {{ $t("employeeprofil.maritalStatus.widowed") }}
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label>{{ $t("employeeprofil.fields.newPassword") }}</label>
          <input v-model="form.password" type="password" class="form-control"/>
        </div>

        <div class="mb-3">
          <label>{{ $t("employeeprofil.fields.confirmPassword") }}</label>
          <input v-model="form.password_confirmation" type="password" class="form-control"/>
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="btn btn-secondary" @click="showModal = false">
            {{ $t("employeeprofil.modal.cancel") }}
          </button>

          <button
            class="btn text-white"
            style="background:#8d2e38;"
            @click="saveProfile"
          >
            {{ $t("employeeprofil.modal.save") }}
          </button>
        </div>

      </div>
    </div>

  </div>
</template>




<script>
import axios from "@/api"
import placeholderImg from "@/assets/img/placeholder.png"

export default {
  name: "EmployeeProfile",

  data() {
    return {
      userData: null,
      personalInfo: null,
      loading: true,
      imgErrored: false,

      userId: JSON.parse(localStorage.getItem("user") || "{}")?.id || null,
      apiBase: "https://api.administration.cap.tn",


      showModal: false,
      form: {
        phone_number: "",
        marital_status: "",
        password: "",
        password_confirmation: ""
      }
    }
  },

  computed: {
    fullName() {
      return `${this.userData?.firstname || ""} ${this.userData?.lastname || ""}`.trim();
    },

    roleLabel() {
      if (this.userData?.role) return this.userData.role;
      const roles = this.userData?.roles;
      if (Array.isArray(roles) && roles.length) {
        if (typeof roles[0] === "object" && roles[0]?.name) return roles[0].name;
        return roles[0];
      }
      return null;
    },

    canEdit() {
      const r = (Array.isArray(this.userData?.roles) && this.userData.roles[0]?.name)
        ? this.userData.roles[0].name
        : (Array.isArray(this.userData?.roles) ? this.userData.roles[0] : this.userData?.role)

      return String(r || "").toLowerCase() === "admin";
    },

  profileImage() {
  if (this.imgErrored) return placeholderImg;

  const p = this.personalInfo?.photo;
  if (!p) return placeholderImg;

  if (/^https?:\/\//i.test(p)) {
    console.log("IMAGE URL USED BY FRONT =", p);
    return p;
  }

  const path = p.replace(/^storage\//, "");
  const url = `${this.apiBase}/storage/${path}`;

  console.log("IMAGE URL USED BY FRONT =", url);
  return url;
}

  },

  mounted() {
    this.load();
  },

  methods: {

    async load() {
      try {
        this.loading = true;
        await Promise.all([this.fetchUserData(), this.fetchPersonalInfo()]);
      } finally {
        this.loading = false;
      }
    },

    async fetchUserData() {
      const { data } = await axios.get(`/getUser/${this.userId}`);
      this.userData = data.user;
    },

    async fetchPersonalInfo() {
      const { data } = await axios.get(`/employee/personal-information/${this.userId}`);
      this.personalInfo = data.data;
    },

    onImgError() {
      this.imgErrored = true;
    },

    openEditModal() {
      this.form.phone_number = this.personalInfo?.phone_number || "";
      this.form.marital_status = this.personalInfo?.marital_status || "";
      this.form.password = "";
      this.form.password_confirmation = "";
      this.showModal = true;
    },

    async saveProfile() {
      try {
        await axios.put("/employee/update-profile", this.form);

        await this.fetchPersonalInfo();
        this.showModal = false;

        alert("Profile updated!");
      } catch (err) {
        console.error(err);
        alert("Error updating profile");
      }
    }
  }
}
</script>



<style scoped>
.header-bar {
  background: linear-gradient(195deg, #4b5563, #8d2e38);
  border-radius: 12px;
}

.export-btn {
  background-color: white;
  color: #b03a45;
  border: 1px solid #b03a45;
  font-weight: 500;
  transition: 0.2s;
}
.export-btn:hover {
  background-color: #b03a45;
  color: white;
}

.employee-card {
  border-radius: 16px;
  box-shadow: 0 8px 18px rgba(0,0,0,0.1);
  transition: 0.3s;
}
.employee-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 20px rgba(0,0,0,0.15);
}


.profile-img-wrapper {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #b03a45;
}

.profile-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.field-label {
  font-size: 0.85rem;
  color: #6b7280;
}
.field-value {
  font-weight: 600;
  color: #111827;
}

/* ------- MODAL ------- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  padding: 24px;
  width: 380px;
  border-radius: 10px;
  max-width: 90%;
}
</style>
